import React, { useState, useEffect } from 'react';
import {Box, Form, Text, Button, CheckBoxGroup, CheckBox, InfiniteScroll} from 'grommet';
import BaseFilter from './BaseFilter';

// `bank_debit` is money we pulled; `bank_transfer` is money the customer sent,
// over either the ACH-credit or the Stripe cash-balance rail. They used to be
// one option filtering on `ach`, which the backend still accepts and which now
// spans both plus more, so a merchant filtering for one direction got the other
// as well. Sentence case throughout, matching the backend's own display_type
// strings ("Bank debit") so a filter and the rows it returns read alike.
const paymentTypes = [
    { label: 'Bank debit', value: 'bank_debit' },
    { label: 'Bank transfer', value: 'bank_transfer' },
    { label: 'Card', value: 'card' },
    { label: 'Check', value: 'check' },
    { label: 'Int. wire', value: 'wire' },
    { label: 'Other payments', value: 'other_payment' },
];

const otherTransactionTypes = [
    { label: 'Credit note', value: 'credit_note' },
    { label: 'Debit note', value: 'debit_note' },
    { label: 'Chargeback', value: 'chargeback' },
    { label: 'Refund', value: 'refund' }
];

const TransactionFilter = ({filterName = "Transaction", filterValues=[], onApplyFilter }) => {
    // Initialize from filterValues prop (for URL-based persistence)
    const initialSelection = Array.isArray(filterValues) ? filterValues : [];
    const [selectedTransactions, setSelectedTransactions] = useState(initialSelection);
    const [allPaymentsSelected, setAllPaymentsSelected] = useState(false);

    // Sync with filterValues prop when it changes (e.g., from URL)
    useEffect(() => {
        const newSelection = Array.isArray(filterValues) ? filterValues : [];
        setSelectedTransactions(newSelection);
    }, [filterValues]);

    useEffect(() => {
        const selectedPaymentTypes = selectedTransactions.filter(type =>
            paymentTypes.some(pt => pt.value === type)
        );
        setAllPaymentsSelected(selectedPaymentTypes.length === paymentTypes.length);
    }, [selectedTransactions]);


    const renderContent = () => (
        <Box gap="xsmall" responsive={false}>
            <Box height={{min: 'small'}} overflow="auto" gap="xsmall">
                <Box margin={{bottom: !allPaymentsSelected ? 'xsmall' : 'none'}}>
                    <CheckBox
                        label="All payments"
                        checked={allPaymentsSelected}
                        onChange={(event) => {
                            if (event.target.checked) {
                                setSelectedTransactions(prev => [
                                    ...new Set([...prev, ...paymentTypes.map(pt => pt.value)])
                                ]);
                            } else {
                                setSelectedTransactions(prev =>
                                    prev.filter(type => !paymentTypes.some(pt => pt.value === type))
                                );
                            }
                        }}
                    />
                    {!allPaymentsSelected && (
                        <Box margin={{top: 'xsmall'}}>
                            <CheckBoxGroup
                                gap="xsmall"
                                options={paymentTypes}
                                value={selectedTransactions}
                                onChange={(event) => setSelectedTransactions(event.value)}
                            />
                        </Box>
                    )}
                </Box>
                <CheckBoxGroup
                    gap="xsmall"
                    options={otherTransactionTypes}
                    value={selectedTransactions}
                    onChange={(event) => setSelectedTransactions(event.value)}
                />
            </Box>
            <Button fill type="submit" primary label="Apply"/>
        </Box>
    );

    const renderButtonLabel = () => {
        if (selectedTransactions.length === 0) return null;

        if (selectedTransactions.length !== 1) return `${selectedTransactions.length} selected`;

        // Guarded because a selection can outlive the option that produced it:
        // these values persist in the URL and in table preferences, and the
        // backend's filter vocabulary changes independently of this list. `ach`
        // is one such value today. Unguarded, find() returns undefined and
        // reading .label took the whole filter down.
        const selected = [...paymentTypes, ...otherTransactionTypes]
            .find(opt => opt.value === selectedTransactions[0]);

        return selected ? selected.label : '1 selected';
    };

    const onApply = () => {
        if (selectedTransactions.length === 0) {
            onReset();
        } else {
            onApplyFilter && onApplyFilter(selectedTransactions);
        }
    }

    const onReset = () => {
        setSelectedTransactions([]);
        setAllPaymentsSelected(false);
        onApplyFilter && onApplyFilter([]);
    }

    return (
        <BaseFilter
            filterName={filterName}
            renderContent={renderContent}
            renderButtonLabel={renderButtonLabel}
            isActive={selectedTransactions.length > 0}
            onApply={onApply}
            onReset={onReset}
        />
    );
};

export default TransactionFilter;
